// src/pages/DustCleanerGamePage.jsx

import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  Home,
  Sparkles,
  Trophy,
} from "lucide-react";

const DustCleanerGamePage = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [percentCleaned, setPercentCleaned] = useState(0);
  const [isWon, setIsWon] = useState(false);

  const [lastPoint, setLastPoint] = useState(null);
  const [currentDirection, setCurrentDirection] = useState(null); 
  const [wrongDirectionCount, setWrongDirectionCount] = useState(0);
  const [directionWarning, setDirectionWarning] = useState(false);

  const cleanImageUrl = "/images/ancient-clean.jpg";

  // =========================
  // วาดฝุ่น
  // =========================
  const drawDust = (ctx, width, height) => {
    ctx.fillStyle = "#8B7355";
    ctx.fillRect(0, 0, width, height);

    for (let i = 0; i < 180; i++) {
      ctx.fillStyle = i % 2 === 0 ? "#6d5a43" : "#9a8265";
      ctx.beginPath();
      ctx.arc(
        Math.random() * width,
        Math.random() * height,
        Math.random() * 20 + 10,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
  };

  // =========================
  // init canvas
  // =========================
  const initCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;

    const ctx = canvas.getContext("2d");
    drawDust(ctx, canvas.width, canvas.height);
  };

  useEffect(() => {
    initCanvas();
    window.addEventListener("resize", initCanvas);
    return () => window.removeEventListener("resize", initCanvas);
  }, []);

  // =========================
  // mouse position
  // =========================
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  // =========================
  // scratch
  // =========================
  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 40, 0, Math.PI * 2);
    ctx.fill();
  };

  // =========================
  // check progress
  // =========================
  const checkProgress = () => {
    if (isWon) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 16) {
      if (pixels[i] === 0) transparentPixels++;
    }

    const percent = Math.round((transparentPixels / (pixels.length / 16)) * 100);
    setPercentCleaned(percent);

    if (percent > 80) {
      setIsWon(true);
    }
  };

  // =========================
  // start drawing
  // =========================
  const startDrawing = (e) => {
    setIsDrawing(true);
    const { x, y } = getMousePos(e);

    setLastPoint({ x, y });
    setCurrentDirection(null); 
    setDirectionWarning(false);
    scratch(x, y);
  };

  // =========================
  // draw
  // =========================
  const draw = (e) => {
    if (!isDrawing) return;

    const { x, y } = getMousePos(e);

    if (lastPoint) {
      const deltaX = x - lastPoint.x;
      
      if (Math.abs(deltaX) < 2) {
        scratch(x, y);
        setLastPoint({ x, y });
        return;
      }

      let isDirectionValid = false;

      if (!currentDirection) {
        const newDirection = deltaX > 0 ? "LTR" : "RTL"; 
        setCurrentDirection(newDirection);
        isDirectionValid = true;
      } else {
        if (currentDirection === "LTR" && deltaX >= -3) {
          isDirectionValid = true;
        } else if (currentDirection === "RTL" && deltaX <= 3) {
          isDirectionValid = true;
        }
      }

      if (isDirectionValid) {
        scratch(x, y);
        setDirectionWarning(false);
        if (Math.random() > 0.8) {
          checkProgress();
        }
        setLastPoint({ x, y }); 
      } else {
        setWrongDirectionCount((prev) => prev + 1);
        setDirectionWarning(true);

        setTimeout(() => {
          setDirectionWarning(false);
        }, 1500);
      }
    } else {
      setLastPoint({ x, y });
    }
  };

  // =========================
  // stop drawing
  // =========================
  const stopDrawing = () => {
    setIsDrawing(false);
    setLastPoint(null);
    setCurrentDirection(null);
    checkProgress();
  };

  // =========================
  // reset
  // =========================
  const resetGame = () => {
    setIsWon(false);
    setPercentCleaned(0);
    setWrongDirectionCount(0);
    setDirectionWarning(false);
    setLastPoint(null);
    setCurrentDirection(null);
    initCanvas();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-yellow-100 overflow-hidden relative select-none">

      {/* ========================= */}
      {/* Navigation */}
      {/* ========================= */}
      <div className="absolute top-2 left-2 md:top-4 md:left-4 z-[60] flex gap-2 md:gap-3">
        <Link
          to="/games"
          className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-3 rounded-xl md:rounded-2xl bg-white shadow-lg border-2 border-amber-200 text-amber-800 text-sm md:text-base font-bold hover:scale-105 transition"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          เกม
        </Link>
        <Link
          to="/"
          className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-3 rounded-xl md:rounded-2xl bg-white shadow-lg border-2 border-amber-200 text-amber-800 text-sm md:text-base font-bold hover:scale-105 transition"
        >
          <Home className="w-4 h-4 md:w-5 md:h-5" />
          หน้าหลัก
        </Link>
      </div>

      {/* ========================= */}
      {/* Mobile Rotate */}
      {/* ========================= */}
      <style>{`
        .rotate-warning { display: none; }
        .game-content { display: flex; }
        @media only screen and (max-width: 900px) and (orientation: portrait) {
          .rotate-warning { display: flex; }
          .game-content { display: none; }
        }
        img { user-drag: none; -webkit-user-drag: none; }
        
        /* สไตล์ Scrollbar เล็กๆ น่ารักๆ สำหรับป๊อปอัป */
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #fcd34d; border-radius: 10px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: #fbbf24; }
      `}</style>

      {/* ========================= */}
      {/* Rotate Screen */}
      {/* ========================= */}
      <div className="rotate-warning fixed inset-0 z-50 bg-amber-900 flex-col items-center justify-center text-white p-8 text-center">
        <div className="text-5xl md:text-7xl mb-4 animate-bounce">↻</div>
        <h2 className="text-2xl md:text-3xl font-black mb-3">กรุณาหมุนหน้าจอ</h2>
        <p className="text-base md:text-lg max-w-md leading-relaxed">
          เกมนี้เล่นได้ดีที่สุดในแนวนอน เพื่อให้เห็นคัมภีร์ใบลานได้ชัดเจนครับ
        </p>
      </div>

      {/* ========================= */}
      {/* Main Game */}
      {/* ========================= */}
      <div className="game-content flex-col items-center justify-center min-h-screen px-2 sm:px-4 py-4 md:py-8">

        {/* Header - ลด Margin บนหน้าจอเล็ก */}
        <div className="text-center mb-3 mt-12 md:mt-16">
          <div className="inline-flex items-center gap-1 md:gap-2 bg-yellow-200 text-yellow-900 px-3 py-1 md:px-5 md:py-2 rounded-full shadow text-xs md:text-base font-bold mb-2 md:mb-4">
            <Sparkles className="w-3 h-3 md:w-5 md:h-5" />
            ภารกิจอนุรักษ์คัมภีร์
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-black text-amber-700 drop-shadow-sm">
            🏺 นักกู้ชีพคัมภีร์ใบลาน
          </h1>
        </div>

        {/* Progress */}
        <div className="w-full max-w-lg mb-3 md:mb-4">
          <div className="flex justify-between items-center mb-1 md:mb-2 text-xs md:text-base font-bold text-amber-800 px-1">
            <span>🧹 ทำความสะอาด</span>
            <span>{percentCleaned}%</span>
          </div>
          <div className="w-full h-4 md:h-6 bg-amber-100 rounded-full overflow-hidden shadow-inner border border-amber-200">
            <div
              className="h-full bg-gradient-to-r from-yellow-300 via-orange-300 to-amber-500 transition-all duration-300"
              style={{ width: `${percentCleaned}%` }}
            />
          </div>
          
          {/* stars */}
          <div className="flex justify-center gap-1 md:gap-2 mt-2 text-lg md:text-2xl">
            <span className={percentCleaned > 20 ? "" : "opacity-30"}>⭐</span>
            <span className={percentCleaned > 50 ? "" : "opacity-30"}>⭐</span>
            <span className={percentCleaned > 80 ? "" : "opacity-30"}>⭐</span>
          </div>
        </div>

        {/* Hint */}
        {!isWon && (
          <div className="mb-2 md:mb-4">
             <div className="bg-white/90 backdrop-blur-sm px-4 py-1.5 md:px-6 md:py-2.5 rounded-full shadow-md text-xs md:text-base font-bold text-amber-800 animate-pulse border border-amber-200">
              👉 ปัดฝุ่นไปทางเดียวกันอย่างเบามือ
            </div>
          </div>
        )}

        {/* Game Area */}
        <div
          ref={containerRef}
          className={`relative w-full max-w-6x2 aspect-[700/180] rounded-xl md:rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.2)] overflow-hidden border-4 md:border-8 transition-colors duration-300 touch-none ${directionWarning ? 'border-red-400' : 'border-amber-300'} bg-white`}
        >
          <img
            src={cleanImageUrl}
            alt="Ancient Document"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none touch-none"
          />
          <canvas
            ref={canvasRef}
            className={`absolute inset-0 touch-none transition-opacity duration-1000 ${
              isWon ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>

        {/* Warning Message */}
        <div className="h-8 md:h-12 mt-2 md:mt-4 flex items-start justify-center">
          {directionWarning && !isWon && (
            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-1.5 md:px-6 md:py-2.5 rounded-full shadow-sm text-xs md:text-base font-bold animate-bounce">
              ⚠️ ถูย้อนทางไม่ได้นะ! ยกมือขึ้นแล้วเริ่มปัดใหม่
            </div>
          )}
        </div>

        {/* Footer Tip */}
        <div className="mt-1 md:mt-2 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl px-4 py-2 md:px-6 md:py-4 shadow-md text-center max-w-xl">
          <p className="text-stone-700 font-semibold text-[10px] md:text-base">
            💡 การดูแลเอกสารโบราณ ต้องปัดฝุ่นไปในทิศทางเดียวกัน 
            เพื่อไม่ให้ฝุ่นฟุ้งกลับมาเกาะที่เดิมนะ
          </p>
          {wrongDirectionCount > 0 && (
            <p className="mt-1 md:mt-2 text-[10px] md:text-sm text-red-500 font-bold">
              ⚠️ ถูย้อนทางไปแล้ว {wrongDirectionCount} ครั้ง
            </p>
          )}
        </div>

      </div>

      {/* ========================= */}
      {/* WIN SCREEN - ปรับลดขนาดและให้ไถเลื่อนได้ */}
      {/* ========================= */}
      {isWon && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          
          <div className="bg-white rounded-3xl p-5 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto custom-scroll shadow-2xl text-center animate-bounce-in flex flex-col items-center">
            
            <div className="flex justify-center mb-2 md:mb-4 shrink-0">
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-yellow-100 flex items-center justify-center shadow-inner">
                <Trophy className="w-8 h-8 md:w-12 md:h-12 text-yellow-500" />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-4xl font-black text-yellow-500 mb-2 shrink-0">
              🌟 เก่งมาก! 🌟
            </h2>
            
            <p className="text-sm md:text-lg text-stone-700 leading-snug md:leading-relaxed mb-3 md:mb-4 shrink-0">
              คัมภีร์ใบลานกลับมาสะอาด
              และพร้อมส่งต่อความรู้โบราณอีกครั้ง
            </p>
            
            <div className="inline-block bg-amber-100 text-amber-800 text-xs md:text-base font-bold px-4 py-1.5 md:px-5 md:py-2 rounded-full mb-4 md:mb-5 shrink-0">
              🏆 นักอนุรักษ์ตัวน้อย
            </div>
            
            <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-3 md:p-4 text-left w-full mb-4 md:mb-6 shrink-0">
              <h3 className="font-black text-orange-700 text-sm md:text-base mb-1 md:mb-2">
                📚 รู้ไหม?
              </h3>
              <p className="text-stone-700 leading-snug md:leading-relaxed text-xs md:text-base">
                คัมภีร์ใบลาน คือหนังสือโบราณที่ชาวล้านนาใช้บันทึกเรื่องราวศาสนาและวิชาความรู้ 
                นักอนุรักษ์จะใช้แปรงขนนุ่มปัดไปในทิศทางเดียวกันเสมอ เพื่อรักษาพื้นผิวใบลานไม่ให้เสียหาย
              </p>
            </div>
            
            <button
              onClick={resetGame}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 md:px-8 md:py-4 rounded-full text-base md:text-lg font-black shadow-lg hover:scale-105 active:scale-95 transition-all shrink-0"
            >
              🔄 เล่นอีกครั้ง
            </button>

          </div>
        </div>
      )}

    </div>
  );
};

export default DustCleanerGamePage;